// ABOUTME: Defines job status transition policy and status-change form behavior.
// ABOUTME: Keeps manager and requester actions consistent across job surfaces.
type ApplicantsByGroupBucket = {
    key?: {
        jobId?: string;
        stageGroup?: string;
    };
    doc_count?: number | string | null;
};

export type AllowedNewStatusType = {
    [K in CompleoShared.Common.JobStatus]?: CompleoShared.Common.JobStatus[];
};

export const allowedManagerJobStatuses: AllowedNewStatusType = {
    open: ["canceled", "finished", "suspended", "awaitingCustomer"],
    draft: ["open", "canceled"],
    requested: [
        "open",
        "approvedRequest",
        "disapprovedRequest",
        "awaitingCorrectionRequest"
    ],
    suspended: ["open", "canceled"],
    awaitingCustomer: ["open", "canceled", "suspended"],
    finished: ["open"],
    canceled: ["open"],
    disapprovedRequest: [],
    approvedRequest: ["open", "canceled"],
    awaitingCorrectionRequest: ["canceled"],
    jobApprovalInProgress: ["canceled"],
    jobApprovalApproved: ["open", "canceled"],
    jobApprovalExpired: ["canceled"],
    jobApprovalRejected: ["canceled"]
};

export const allowedRequesterJobStatuses: AllowedNewStatusType = {
    open: [],
    draft: [],
    requested: [],
    suspended: [],
    awaitingCustomer: [],
    finished: [],
    canceled: [],
    awaitingCorrectionRequest: ["requested", "canceled"],
    disapprovedRequest: [],
    approvedRequest: [],
    jobApprovalExpired: ["canceled", "jobApprovalRequested"],
    jobApprovalRejected: ["canceled", "jobApprovalRequested"]
};

export const isJobStatusChangeAllowed = (
    currentStatus: CompleoShared.Common.JobStatus,
    newStatus: CompleoShared.Common.JobStatus,
    isJobRequestList: boolean
): boolean => {
    const allowedStatuses = isJobRequestList
        ? allowedRequesterJobStatuses
        : allowedManagerJobStatuses;

    return allowedStatuses[currentStatus]?.includes(newStatus) === true;
};

export const requiresJobStatusChangeDetails = (
    newStatus: CompleoShared.Common.JobStatus,
    oldStatus: CompleoShared.Common.JobStatus
): boolean => {
    if (
        [
            "finished",
            "canceled",
            "approvedRequest",
            "disapprovedRequest",
            "suspended",
            "requested",
            "awaitingCorrectionRequest",
            "jobApprovalRequested"
        ].includes(newStatus)
    ) {
        return true;
    }

    return (
        newStatus === "open" &&
        [
            "finished",
            "canceled",
            "draft",
            "requested",
            "approvedRequest",
            "suspended"
        ].includes(oldStatus)
    );
};

export const getJobStatusChangeModule = (
    newStatus: CompleoShared.Common.JobStatus,
    oldStatus: CompleoShared.Common.JobStatus
): string => {
    switch (newStatus) {
        case "finished":
            return "CHANGEJOBSTATUSTOFINISHED";
        case "canceled":
            return "CHANGEJOBSTATUSTOCANCELED";
        case "awaitingCorrectionRequest":
            return "CHANGEJOBSTATUSTOAWAITINGCORRECTIONREQUEST";
        case "open":
            return [
                "draft",
                "requested",
                "approvedRequest",
                "suspended"
            ].includes(oldStatus)
                ? "CHANGEJOBSTATUSFROMDRAFTTOOPEN"
                : "CHANGEJOBSTATUSTOOPEN";
        case "approvedRequest":
            return "CHANGEJOBSTATUSTOAPPROVED";
        case "disapprovedRequest":
            return "CHANGEJOBSTATUSTODISAPPROVED";
        case "suspended":
            return "CHANGEJOBSTATUSTOSUSPENDED";
        case "requested":
            return "CHANGEJOBSTATUSTOREQUESTED";
        case "jobApprovalRequested":
            if (oldStatus === "jobApprovalExpired") {
                return "CHANGEJOBSTATUSFROMJOBAPPROVALEXPIREDTOJOBAPPROVALREQUESTED";
            }
            if (oldStatus === "jobApprovalRejected") {
                return "CHANGEJOBSTATUSFROMJOBAPPROVALREJECTEDTOJOBAPPROVALREQUESTED";
            }
            return "";
        default:
            return "";
    }
};

export const getJobStatusChangeInitialOpeningDate = (
    oldStatus: CompleoShared.Common.JobStatus,
    currentDate: Date,
    openingDate?: Date | string
): Date | string | undefined => {
    if (["draft", "requested", "approvedRequest"].includes(oldStatus)) {
        return currentDate;
    }
    if (oldStatus === "suspended") {
        return openingDate || currentDate;
    }
    return undefined;
};

export const getApplicantsInHiredGroup = (
    applicantsByGroup: ApplicantsByGroupBucket[] = [],
    jobId?: string
): number => {
    return applicantsByGroup.reduce((total, item) => {
        if (item?.key?.stageGroup !== "hired") {
            return total;
        }

        if (jobId && item?.key?.jobId && item.key.jobId !== jobId) {
            return total;
        }

        const count = Number(item?.doc_count || 0);
        return Number.isFinite(count) ? total + count : total;
    }, 0);
};
